import React, { useState, useRef, useMemo } from 'react'

import Box from '@mui/material/Box'
import { useIntl } from 'react-intl'
import Step from '@mui/material/Step'
import Stepper from '@mui/material/Stepper'
import AddIcon from '@mui/icons-material/Add'
import StepLabel from '@mui/material/StepLabel'
import { captureException } from '@sentry/react'
import { LoanRole } from '@matech/thebigpos-sdk'
import Typography from '@mui/material/Typography'
import type { AutocompleteInputChangeReason } from '@mui/material/Autocomplete'

import Dialog from './Dialog'
import ShareLoanUserForm, {
	type ShareLoanUserRef,
	type HandleSubmitAddUserParams,
} from '../ShareLoanUserForm'
import { Button } from '../Button'
import UsersSelect from '../UsersSelect'
import useUser from '../../hooks/useUser'
import Select from '../formControls/Select'
import { UserRole } from './ModalShareLoan'
import { TextField } from '../formControls'
import { LabelValuePair } from '../../types'
import { Roles } from '../../services/client'
import BranchesSelect from '../BranchesSelect'
import FormControl from '../formControls/FormControl'
import ShareLoanList from '../ShareLoanList/ShareLoanList'
import { type SelectChangeEvent } from '../formControls/Select'
// ToDo: Fix this cyclical dependencies in all places that is calling this component
// Task Name: [WEB] - Fix circular dependence imports in Share Loan feature
// Task ID: 9880243555
// Task Link: https://mortgage-automation-technologies.monday.com/boards/5842126081/pulses/9880243555
import type { LoanUserRow } from '../loanAppDetails/LoanUsersTable'
import ModalUserGroupsAccessList from '../../pages/admin/UserGroups/components/ModalUserGroupsAccess/ModalUserGroupsAccessList'

const USERS_FIELD_FILTER_ROLES = [
	Roles.loanOfficer,
	Roles.loanProcessor,
	Roles.loanOfficerAssistant,
]

const USERS_FIELD_FILTER_ROLES_ACCESS = [
	Roles.loanOfficer,
	Roles.loanProcessor,
]

type AccessItemType = 'Branch' | 'User'

export const ACCESS_ITEM_TYPES = {
	BRANCH: 'Branch' as const,
	USER: 'User' as const,
} satisfies Record<string, AccessItemType>

export type UserGroupScopeItem = {
	id: string
	value: string
	label: string
	type: AccessItemType
	role?: LoanRole
}

export type UserGroupFormData = {
	groupName: string
	description: string
	selectedUsers: UserRole[]
	accessItems: UserGroupScopeItem[]
}

type ModalUserGroupsMembersFormValues = {
	selectedUsersRoles?: UserRole[]
	groupName?: string
	description?: string | null
	initialAccessItems?: UserGroupScopeItem[]
}

// ToDo: Unify this type to use in ModalShareLoan
export type ModalUserGroupsFormProps = Readonly<{
	visible: boolean
	onClose: () => void
	addUser?: {
		onBeforeAdd?: (user: LoanUserRow, role: LoanRole) => boolean
		onAdd?: (user: LoanUserRow, role: LoanRole) => void
		isLoading?: boolean
	}
	roleChange?: {
		onChange?: (userId: string, role: LoanRole) => void
	}
	revokeUser?: {
		onRevoke?: (userId: string) => void
		isLoading?: boolean
		confirmRemove?: boolean
	}
	submit?: {
		onSubmit?: (data: UserGroupFormData) => Promise<void>
		isLoading?: boolean
	}
	isLoading?: boolean
	showActions?: boolean
	initialValues?: ModalUserGroupsMembersFormValues
}>

// ToDo: Check about unify methods into a hook to in ModalShareLoan
const ModalUserGroupsForm = ({
	visible,
	onClose,
	addUser,
	revokeUser,
	roleChange,
	submit,
	showActions = true,
	isLoading,
	initialValues,
}: ModalUserGroupsFormProps) => {
	const { user } = useUser()
	const { formatMessage } = useIntl()
	const autocompleteRef = useRef<HTMLDivElement>(null)
	const shareLoanUserFormRef = useRef<ShareLoanUserRef>(null)

	const modalTitle = formatMessage({
		id: 'user_groups.title',
	})

	const [activeStep, setActiveStep] = useState(0)
	const [groupName, setGroupName] = useState<string>('')
	const [description, setDescription] = useState<string>('')
	const [groupNameError, setGroupNameError] = useState<string>('')
	const [selectedLocalUserList, setSelectedLocalUserList] = useState<
		UserRole[]
	>([])

	const [inputValue, setInputValue] = useState('')
	const [branchInputValue, setBranchInputValue] = useState('')
	const [validationErrors, setValidationErrors] = useState<{
		userMessageKey?: string
		roleMessageKey?: string
		duplicateMessageKey?: string
		userAccessRequiredMessageKey?: string
		branchAccessRequiredMessageKey?: string
	}>({})
	const [selectedLocalUser, setSelectedLocalUser] =
		useState<LoanUserRow | null>(null)
	const [selectedLocalType, setSelectedLocalType] =
		useState<AccessItemType>(ACCESS_ITEM_TYPES.USER)
	const [selectedLocalBranch, setSelectedLocalBranch] =
		useState<LabelValuePair | null>(null)
	const [accessItemsList, setAccessItemsList] = useState<
		UserGroupScopeItem[]
	>([])

	const disableTableButtons =
		addUser?.isLoading || revokeUser?.isLoading
	const disabledSave = !selectedLocalUserList[0]

	const disableAddUserButton =
		(selectedLocalType === ACCESS_ITEM_TYPES.USER &&
			!selectedLocalUser) ||
		(selectedLocalType === ACCESS_ITEM_TYPES.BRANCH &&
			!selectedLocalBranch) ||
		submit?.isLoading

	const typeMenuItems = useMemo(
		() => [
			{
				value: ACCESS_ITEM_TYPES.BRANCH,
				label: formatMessage({
					id: 'entity_type.branch',
				}),
			},
			{
				value: ACCESS_ITEM_TYPES.USER,
				label: formatMessage({
					id: 'entity_type.user',
				}),
			},
		],
		[formatMessage]
	)

	const handleGroupNameChange = (
		e: React.ChangeEvent<HTMLInputElement>
	) => {
		setGroupName(e.target.value)
		if (groupNameError) setGroupNameError('')
	}

	const handleDescriptionChange = (
		e: React.ChangeEvent<HTMLInputElement>
	) => setDescription(e.target.value)

	const handleDialogExit = () => {
		setSelectedLocalUserList([])
		setGroupName('')
		setDescription('')
		setGroupNameError('')
		setActiveStep(0)
		setSelectedLocalUser(null)
		setSelectedLocalType(ACCESS_ITEM_TYPES.USER)
		setSelectedLocalBranch(null)
		setInputValue('')
		setBranchInputValue('')
		setAccessItemsList([])
		setValidationErrors({})
	}

	const handleDialogEnter = () => {
		if (initialValues) {
			const {
				groupName,
				description,
				selectedUsersRoles,
				initialAccessItems,
			} = initialValues

			setGroupName(groupName ?? '')
			setDescription(description ?? '')
			setSelectedLocalUserList(selectedUsersRoles ?? [])
			setAccessItemsList(initialAccessItems ?? [])
		}
	}

	const handleNext = () => {
		// Validate group name before proceeding to next step
		if (activeStep === 0) {
			setGroupNameError('')

			if (!groupName.trim()) {
				setGroupNameError('Group name is required')
				return
			}
		}

		setActiveStep((prevActiveStep) => prevActiveStep + 1)
	}

	const handleBack = () =>
		setActiveStep((prevActiveStep) => prevActiveStep - 1)

	const steps = [
		{
			label: formatMessage({
				id: 'user_groups.steps.group_setup',
				defaultMessage: 'Group Setup',
			}),
		},
		{
			label: formatMessage({
				id: 'user_groups.steps.permissions',
				defaultMessage: 'Access Scope',
			}),
		},
	]

	const handleTypeChange = (e: SelectChangeEvent<unknown>) => {
		setSelectedLocalType(e.target.value as AccessItemType)
		setValidationErrors({})
		setSelectedLocalUser(null)
		setSelectedLocalBranch(null)
		setInputValue('')
		setBranchInputValue('')

		if (validationErrors.roleMessageKey)
			setValidationErrors((prev) => ({
				...prev,
				role: undefined,
			}))
	}

	const handleInputChange = (
		value: string,
		reason: AutocompleteInputChangeReason
	) => {
		if (reason === 'clear') {
			setSelectedLocalUser(null)
			setInputValue('')
			return
		}

		setInputValue(value)
	}

	const handleBranchInputChange = (
		value: string,
		reason: AutocompleteInputChangeReason
	) => {
		if (reason === 'clear') {
			setSelectedLocalBranch(null)
			setBranchInputValue('')
			return
		}

		setBranchInputValue(value)
	}

	const handleAutocompleteClose = () => {
		if (!selectedLocalUser && inputValue) setInputValue('')
	}

	const handleBranchAutocompleteClose = () => {
		if (!selectedLocalBranch && branchInputValue)
			setBranchInputValue('')
	}

	const handleChangeUser = (value: LoanUserRow | null) => {
		setSelectedLocalUser(value)

		if (
			validationErrors.userMessageKey ||
			validationErrors.duplicateMessageKey
		)
			setValidationErrors((prev) => ({
				...prev,
				userMessageKey: undefined,
				duplicateMessageKey: undefined,
			}))

		if (value && selectedLocalType === ACCESS_ITEM_TYPES.USER) {
			const isDuplicate = accessItemsList.some(
				(item) =>
					item.type === ACCESS_ITEM_TYPES.USER &&
					item.value === value.id
			)

			if (isDuplicate)
				setValidationErrors((prev) => ({
					...prev,
					duplicateMessageKey:
						'share_loan.validation.user_already_added',
				}))
		}
	}

	const handleChangeBranch = (value: LabelValuePair | null) => {
		setSelectedLocalBranch(value)

		if (
			validationErrors.userMessageKey ||
			validationErrors.duplicateMessageKey
		)
			setValidationErrors((prev) => ({
				...prev,
				userMessageKey: undefined,
				duplicateMessageKey: undefined,
			}))

		if (value && selectedLocalType === ACCESS_ITEM_TYPES.BRANCH) {
			const isDuplicate = accessItemsList.some(
				(item) =>
					item.type === ACCESS_ITEM_TYPES.BRANCH &&
					item.value === value.value
			)

			if (isDuplicate)
				setValidationErrors((prev) => ({
					...prev,
					duplicateMessageKey:
						'share_loan.validation.branch_already_added',
				}))
		}
	}

	const validateAccessInput = () => {
		const errors: {
			userMessageKey?: string
			duplicateMessageKey?: string
		} = {}

		if (
			selectedLocalType === ACCESS_ITEM_TYPES.USER &&
			!selectedLocalUser
		)
			errors.userMessageKey =
				'share_loan.validation.please_select_user'

		if (
			selectedLocalType === ACCESS_ITEM_TYPES.BRANCH &&
			!selectedLocalBranch
		)
			errors.userMessageKey =
				'share_loan.validation.please_select_branch'

		if (selectedLocalUser?.id === user.id)
			errors.userMessageKey =
				'share_loan.validation.cannot_select_self'

		if (
			selectedLocalType === ACCESS_ITEM_TYPES.USER &&
			selectedLocalUser
		) {
			const isDuplicate = accessItemsList.some(
				(item) =>
					item.type === ACCESS_ITEM_TYPES.USER &&
					item.value === selectedLocalUser.id
			)

			if (isDuplicate)
				errors.duplicateMessageKey =
					'share_loan.validation.user_already_added'
		}

		if (
			selectedLocalType === ACCESS_ITEM_TYPES.BRANCH &&
			selectedLocalBranch
		) {
			const isDuplicate = accessItemsList.some(
				(item) =>
					item.type === ACCESS_ITEM_TYPES.BRANCH &&
					item.value === selectedLocalBranch.value
			)

			if (isDuplicate)
				errors.duplicateMessageKey =
					'share_loan.validation.branch_already_added'
		}

		setValidationErrors(errors)
		return Object.keys(errors).length > 0
	}

	const handleAddAccessItem = async () => {
		const hasErrors = validateAccessInput()

		if (hasErrors) return

		if (
			selectedLocalType === ACCESS_ITEM_TYPES.USER &&
			!selectedLocalUser
		)
			return

		if (
			selectedLocalType === ACCESS_ITEM_TYPES.BRANCH &&
			!selectedLocalBranch
		)
			return

		let newAccessItem: UserGroupScopeItem
		const id = crypto.randomUUID()

		if (selectedLocalType === ACCESS_ITEM_TYPES.USER) {
			newAccessItem = {
				id,
				value: selectedLocalUser!.id,
				label: selectedLocalUser!.name,
				role: selectedLocalUser?.role,
				type: ACCESS_ITEM_TYPES.USER,
			}
		} else {
			newAccessItem = {
				id,
				value: String(selectedLocalBranch!.value),
				label: selectedLocalBranch!.label,
				type: ACCESS_ITEM_TYPES.BRANCH,
			}
		}

		setAccessItemsList((prev) => [...prev, newAccessItem])

		if (
			validationErrors.userAccessRequiredMessageKey ||
			validationErrors.branchAccessRequiredMessageKey
		)
			setValidationErrors((prev) => ({
				...prev,
				userAccessRequiredMessageKey: undefined,
				branchAccessRequiredMessageKey: undefined,
			}))

		setSelectedLocalUser(null)
		setSelectedLocalBranch(null)
		setInputValue('')
		setBranchInputValue('')
	}

	const handleDeleteAccessItem = (itemId: string) => {
		setAccessItemsList((prev) =>
			prev.filter((item) => item.id !== itemId)
		)

		if (validationErrors.duplicateMessageKey)
			setValidationErrors((prev) => ({
				...prev,
				duplicateMessageKey: undefined,
			}))
	}

	const handleRevokeUser = async (userId: string) => {
		try {
			if (revokeUser?.confirmRemove) {
				setSelectedLocalUserList((prev) =>
					prev.map((userRole) =>
						userRole.user.id === userId
							? {
									...userRole,
									markedForDeletion: !userRole.markedForDeletion,
								}
							: userRole
					)
				)
				return
			}

			if (revokeUser?.onRevoke) {
				revokeUser.onRevoke(userId)
				return
			}

			setSelectedLocalUserList((prev) =>
				prev.filter((userRole) => userRole.user.id !== userId)
			)
		} catch (error) {
			captureException(
				`Fail to revoke user: ${JSON.stringify(error)}`
			)
		}
	}

	const handleAddUser = ({
		user,
		role,
	}: HandleSubmitAddUserParams) => {
		if (addUser?.onBeforeAdd) {
			const resultBeforeAddUser = addUser?.onBeforeAdd(user, role)

			if (!resultBeforeAddUser) return
		}

		const userExists = selectedLocalUserList.some(
			(item) => item.user.id === user.id
		)

		if (userExists) {
			shareLoanUserFormRef.current?.setUserErrorMessageKey(
				'share_loan.validation.user_already_added'
			)
			return
		}

		if (addUser?.onAdd) {
			addUser?.onAdd(user, role)
		} else {
			setSelectedLocalUserList((prev) => [
				...prev,
				{
					user,
					assignedRole: role,
				},
			])
		}

		shareLoanUserFormRef.current?.clearForm()
	}

	const handleClose = (
		_event?: object,
		reason?: 'backdropClick' | 'escapeKeyDown'
	) => {
		if (addUser?.isLoading) return

		if (reason === 'backdropClick') return

		onClose()
	}

	const handleSubmit = async () => {
		let hasErrors = false

		if (
			activeStep === steps.length - 1 &&
			accessItemsList.length === 0
		) {
			if (selectedLocalType === ACCESS_ITEM_TYPES.USER) {
				setValidationErrors((prev) => ({
					...prev,
					userAccessRequiredMessageKey:
						'user_groups.validation.user_access_required',
				}))
			} else {
				setValidationErrors((prev) => ({
					...prev,
					branchAccessRequiredMessageKey:
						'user_groups.validation.branch_access_required',
				}))
			}
			hasErrors = true
		} else {
			setValidationErrors((prev) => ({
				...prev,
				userAccessRequiredMessageKey: undefined,
				branchAccessRequiredMessageKey: undefined,
			}))
		}

		if (
			hasErrors ||
			!selectedLocalUserList ||
			selectedLocalUserList.length === 0
		)
			return

		await submit?.onSubmit?.({
			groupName,
			description,
			selectedUsers: selectedLocalUserList,
			accessItems: accessItemsList,
		})
	}

	const handleRoleChange = (
		e: SelectChangeEvent<unknown>,
		userId: string
	) => {
		const assignedRole = e.target.value as LoanRole

		if (roleChange?.onChange) {
			roleChange?.onChange(userId, assignedRole)
			return
		}

		setSelectedLocalUserList((prev) =>
			prev.map((userRole) =>
				userRole.user.id === userId
					? {
							...userRole,
							assignedRole,
						}
					: userRole
			)
		)
	}

	return (
		<Dialog
			aria-labelledby="share-loan-dialog-title"
			title={modalTitle}
			open={visible}
			fullWidth
			maxWidth="md"
			onClose={handleClose}
			showActions={showActions}
			closeButtonProps={{
				text: formatMessage({
					id: activeStep === 0 ? 'global.cancel' : 'global.back',
					defaultMessage: activeStep === 0 ? 'Cancel' : 'Back',
				}),
				onClick: activeStep === 0 ? handleClose : handleBack,
				disabled: disableTableButtons || submit?.isLoading,
			}}
			submitButtonProps={{
				text: formatMessage({
					id:
						activeStep === steps.length - 1
							? 'global.save'
							: 'global.next',
					defaultMessage:
						activeStep === steps.length - 1 ? 'Save' : 'Next',
				}),
				onClick:
					activeStep === steps.length - 1 ? handleSubmit : handleNext,
				loading: submit?.isLoading,
				disabled:
					submit?.isLoading || (activeStep === 0 && disabledSave),
			}}
			TransitionProps={{
				onEnter: handleDialogEnter,
				onExit: handleDialogExit,
			}}
		>
			<Stepper activeStep={activeStep} orientation="horizontal">
				{steps.map((step) => (
					<Step key={step.label}>
						<StepLabel>{step.label}</StepLabel>
					</Step>
				))}
			</Stepper>

			<Box sx={{ mt: 3 }}>
				{activeStep === 0 && (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 2,
						}}
					>
						<Box sx={{ display: 'flex', gap: 2 }}>
							<TextField
								label={formatMessage({
									id: 'user_groups.fields.group_name',
								})}
								required
								disabled={submit?.isLoading}
								value={groupName}
								onChange={handleGroupNameChange}
								error={!!groupNameError}
								helperText={groupNameError}
							/>
							<TextField
								label={formatMessage({
									id: 'user_groups.fields.description',
								})}
								sx={{ flex: 1 }}
								value={description}
								disabled={submit?.isLoading}
								onChange={handleDescriptionChange}
							/>
						</Box>

						<p className="text-md font-semibold mt-1">
							{formatMessage({
								id: 'user_groups.add_group_members',
								defaultMessage: 'Add group members',
							})}
						</p>

						<ShareLoanUserForm
							loanUserFormRef={shareLoanUserFormRef}
							loading={addUser?.isLoading}
							disabled={disableTableButtons || submit?.isLoading}
							showRolesBasedOnUserRole
							filterRolesUserField={USERS_FIELD_FILTER_ROLES}
							handleSubmitAddUser={handleAddUser}
						/>

						<ShareLoanList
							userId={user.id}
							loading={isLoading}
							showRolesBasedOnUserRole
							isSubmitting={submit?.isLoading}
							onRevokeUser={handleRevokeUser}
							onChangeRoleItem={handleRoleChange}
							selectedUserRoles={selectedLocalUserList}
						/>
					</Box>
				)}

				{activeStep === 1 && (
					<Box
						sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
					>
						<p className="text-md font-semibold mt-1">
							{formatMessage({
								id: 'user_groups.configure_access_scope',
								defaultMessage: 'Configure access scope',
							})}
						</p>
						<Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
							<Box sx={{ flex: 1 }}>
								<FormControl
									variant="standard"
									fullWidth
									required
									error={!!validationErrors.roleMessageKey}
								>
									<Select
										label={formatMessage({
											id: 'global.type',
										})}
										disabled={submit?.isLoading}
										menuItems={typeMenuItems}
										controlled={false}
										selectProps={{
											value: selectedLocalType,
											onChange: handleTypeChange,
										}}
									/>

									{validationErrors.roleMessageKey && (
										<Typography variant="caption" color="error">
											{formatMessage({
												id: validationErrors.roleMessageKey,
											})}
										</Typography>
									)}
								</FormControl>
							</Box>

							{selectedLocalType === ACCESS_ITEM_TYPES.USER && (
								<Box sx={{ flex: 2 }}>
									<UsersSelect
										value={selectedLocalUser}
										textFieldRef={autocompleteRef}
										onOptionChange={handleChangeUser}
										onInputChange={handleInputChange}
										onOptionClose={handleAutocompleteClose}
										filterRoles={USERS_FIELD_FILTER_ROLES_ACCESS}
										error={
											!!validationErrors.userMessageKey ||
											!!validationErrors.duplicateMessageKey ||
											!!validationErrors.userAccessRequiredMessageKey
										}
										helperText={formatMessage({
											id:
												validationErrors.userMessageKey ??
												validationErrors.duplicateMessageKey ??
												validationErrors.userAccessRequiredMessageKey ??
												'share_loan.search_and_select_user',
										})}
									/>
								</Box>
							)}

							{selectedLocalType === ACCESS_ITEM_TYPES.BRANCH && (
								<Box sx={{ flex: 2 }}>
									<BranchesSelect
										value={selectedLocalBranch}
										onOptionChange={handleChangeBranch}
										onInputChange={handleBranchInputChange}
										onOptionClose={handleBranchAutocompleteClose}
										error={
											!!validationErrors.userMessageKey ||
											!!validationErrors.duplicateMessageKey ||
											!!validationErrors.branchAccessRequiredMessageKey
										}
										helperText={
											validationErrors.userMessageKey ||
											validationErrors.duplicateMessageKey ||
											validationErrors.branchAccessRequiredMessageKey
												? formatMessage({
														id:
															validationErrors.userMessageKey ??
															validationErrors.duplicateMessageKey ??
															validationErrors.branchAccessRequiredMessageKey,
													})
												: formatMessage({
														id: 'global.search_and_select_branch',
													})
										}
									/>
								</Box>
							)}

							<Box sx={{ display: 'flex', alignItems: 'center' }}>
								<Button
									text={formatMessage({
										id: 'global.add',
									})}
									onClick={handleAddAccessItem}
									disabled={disableAddUserButton}
									startIcon={<AddIcon />}
								/>
							</Box>
						</Box>

						<ModalUserGroupsAccessList
							loading={isLoading}
							isSubmitting={submit?.isLoading}
							accessItems={accessItemsList}
							onDeleteItem={handleDeleteAccessItem}
						/>
					</Box>
				)}
			</Box>
		</Dialog>
	)
}

export default ModalUserGroupsForm
